/**
 * Moteur de recherche et d'OCR hybride haute performance pour Docarn Juridique 2.0
 * 
 * Fonctionnalités :
 * 1. Détection automatique : texte numérique natif vs document scanné.
 * 2. Reconstruction avancée du texte PDF.js (correction des espaces inter-lettres, ligatures, césures).
 * 3. Fallback OCR client-side haute précision (Tesseract.js WebAssembly en Web Worker).
 * 4. Prétraitement d'image (seuillage adaptatif, rehaussement de contraste, nettoyage du grain).
 * 5. Recherche insensible aux accents, à la casse et aux variations d'espacement.
 * 6. Cache persistant en mémoire par document.
 */

export interface TextSearchResult {
  id: string;
  pageNumber: number;
  snippetBefore: string;
  matchText: string;
  snippetAfter: string;
  matchIndex: number;
  isOcr?: boolean;
}

export interface OcrProgressInfo {
  pageNumber: number;
  percent: number;
  status: string;
  isOcrActive: boolean;
}

// Singleton pour le chargement dynamique de Tesseract.js (zéro impact sur le bundle initial)
let tesseractLoaderPromise: Promise<any> | null = null;

export const loadTesseract = async (): Promise<any> => {
  if (typeof window === 'undefined') return null;
  if ((window as any).Tesseract) return (window as any).Tesseract;
  if (tesseractLoaderPromise) return tesseractLoaderPromise;

  tesseractLoaderPromise = new Promise((resolve, reject) => {
    // Vérifier si la balise existe déjà
    const existing = document.querySelector('script[data-tesseract="true"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).Tesseract));
      existing.addEventListener('error', (err) => reject(err));
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('data-tesseract', 'true');
    script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      if ((window as any).Tesseract) {
        resolve((window as any).Tesseract);
      } else {
        reject(new Error("Module Tesseract non trouvé après chargement."));
      }
    };

    script.onerror = (err) => {
      tesseractLoaderPromise = null;
      reject(new Error("Impossible de charger le script OCR Tesseract.js depuis le CDN."));
    };

    document.head.appendChild(script);
  });

  return tesseractLoaderPromise;
};

/**
 * Reconstruit le texte d'une page PDF à partir des items bruts PDF.js.
 * Élimine le problème classique de PDF.js où chaque lettre est un item séparé avec espace ("A r t i c l e").
 */
export function reconstructPdfText(textContent: any): string {
  if (!textContent || !Array.isArray(textContent.items) || textContent.items.length === 0) {
    return '';
  }

  const items = textContent.items.filter((it: any) => typeof it.str === 'string' && it.str.length > 0);
  if (items.length === 0) return '';

  // Regrouper par lignes selon la coordonnée Y (avec tolérance de 3.5px)
  const lines: { y: number; items: any[] }[] = [];

  for (const it of items) {
    const y = it.transform ? it.transform[5] : 0;
    const existingLine = lines.find(l => Math.abs(l.y - y) <= 3.5);
    if (existingLine) {
      existingLine.items.push(it);
    } else {
      lines.push({ y, items: [it] });
    }
  }

  // Trier les lignes du haut vers le bas
  lines.sort((a, b) => b.y - a.y);

  const lineTexts: string[] = [];

  for (const line of lines) {
    // Trier les items de gauche à droite
    line.items.sort((a, b) => {
      const xA = a.transform ? a.transform[4] : 0;
      const xB = b.transform ? b.transform[4] : 0;
      return xA - xB;
    });

    let lineStr = '';
    let lastXEnd = -1;

    for (const it of line.items) {
      const x = it.transform ? it.transform[4] : 0;
      const width = it.width || (it.str.length * 6);
      const str = it.str;

      if (lastXEnd === -1) {
        lineStr += str;
      } else {
        const gap = x - lastXEnd;
        // Si l'écart horizontal est supérieur à 2.8px et qu'il n'y a pas déjà d'espace, insérer un espace
        if (gap > 2.8 && !lineStr.endsWith(' ') && !str.startsWith(' ')) {
          lineStr += ' ' + str;
        } else {
          lineStr += str;
        }
      }
      lastXEnd = x + width;
    }

    const trimmed = lineStr.trim();
    if (trimmed.length > 0) {
      lineTexts.push(trimmed);
    }
  }

  // Fusionner les lignes avec gestion intelligente des césures (ex: "admi-" + "nistration" -> "administration")
  let result = '';
  for (let i = 0; i < lineTexts.length; i++) {
    const cur = lineTexts[i];
    if (i < lineTexts.length - 1) {
      if (cur.endsWith('-') && /[a-zA-ZÀ-ÿ]-$/.test(cur)) {
        result += cur.slice(0, -1);
      } else {
        result += cur + '\n';
      }
    } else {
      result += cur;
    }
  }

  // Nettoyage des caractères de ponctuation et apostrophes typographiques
  return result
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u00A0\u202F\u2007\u200B]/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

/**
 * Prétraitement d'image sur Canvas :
 * Binarisation adaptative & contraste pour sublimer les scans de journaux officiels (fond jauni, bavures, ombres).
 */
export function preprocessCanvasForOcr(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return canvas;

  try {
    const width = canvas.width;
    const height = canvas.height;
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;

    let min = 255;
    let max = 0;

    // Détection de l'étendue de gris
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (gray < min) min = gray;
      if (gray > max) max = gray;
    }

    const range = Math.max(25, max - min);

    // Binarisation avec étirement dynamique
    for (let i = 0; i < data.length; i += 4) {
      const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      const normalized = ((gray - min) / range) * 255;
      const val = normalized < 145 ? 0 : 255;
      data[i] = val;
      data[i + 1] = val;
      data[i + 2] = val;
    }

    ctx.putImageData(imgData, 0, 0);
  } catch (err) {
    console.warn("Erreur prétraitement canvas OCR:", err);
  }

  return canvas;
}

export interface OcrWordBox {
  text: string;
  bbox: { x0: number; y0: number; x1: number; y1: number };
}

export interface OcrPageResult {
  text: string;
  words: OcrWordBox[];
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * Exécute l'OCR haute fidélité côté client sur une page scannée avec Tesseract.js.
 * Renvoie le texte complet nettoyé ainsi que la boîte englobante de chaque mot (bbox)
 * pour permettre le surlignage visuel direct sur le scan.
 */
export async function extractPageTextWithOcr(
  page: any,
  onProgress?: (info: { percent: number; status: string }) => void
): Promise<OcrPageResult> {
  try {
    const Tesseract = await loadTesseract();
    if (!Tesseract) return { text: '', words: [], canvasWidth: 0, canvasHeight: 0 };

    // Rendu haute résolution (scale 2.0) pour une netteté maximale
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { text: '', words: [], canvasWidth: 0, canvasHeight: 0 };

    await page.render({ canvasContext: ctx, viewport }).promise;

    // Prétraitement de l'image
    preprocessCanvasForOcr(canvas);

    // Reconnaissance Tesseract multilingue (français juridique + anglais)
    const result = await Tesseract.recognize(
      canvas,
      'fra+eng',
      {
        logger: (m: any) => {
          if (onProgress && m.status === 'recognizing text' && typeof m.progress === 'number') {
            onProgress({
              percent: Math.round(m.progress * 100),
              status: `Docarn AI : Déchiffrement en cours ${Math.round(m.progress * 100)}%`
            });
          }
        }
      }
    );

    const raw = result?.data?.text || '';
    const cleanText = raw
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/\r\n/g, '\n')
      .replace(/[ \t]+/g, ' ')
      .trim();

    const rawWords = result?.data?.words || [];
    const words: OcrWordBox[] = rawWords
      .map((w: any) => ({
        text: String(w.text || '').replace(/[\u2018\u2019]/g, "'").trim(),
        bbox: w.bbox || { x0: 0, y0: 0, x1: 0, y1: 0 },
      }))
      .filter((w: OcrWordBox) => w.text.length > 0);

    return {
      text: cleanText,
      words,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    };
  } catch (err) {
    console.warn("Échec extraction OCR client :", err);
    return { text: '', words: [], canvasWidth: 0, canvasHeight: 0 };
  }
}

/**
 * Construit une expression régulière insensible aux accents, à la casse et aux espaces
 * pour surligner toutes les correspondances d'une requête.
 */
export function createAccentInsensitiveRegex(query: string): RegExp | null {
  if (!query || query.trim().length === 0) return null;

  const escapeRegex = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

  const charToPattern = (ch: string) => {
    const lower = ch.toLowerCase();
    switch (lower) {
      case 'a':
      case 'à':
      case 'â':
      case 'ä':
        return '[aàâäAÀÂÄ]';
      case 'e':
      case 'é':
      case 'è':
      case 'ê':
      case 'ë':
        return '[eéèêëEÉÈÊË]';
      case 'i':
      case 'î':
      case 'ï':
        return '[iîïIÎÏ]';
      case 'o':
      case 'ô':
      case 'ö':
        return '[oôöOÔÖ]';
      case 'u':
      case 'ù':
      case 'û':
      case 'ü':
        return '[uùûüUÙÛÜ]';
      case 'c':
      case 'ç':
        return '[cçCÇ]';
      default:
        return escapeRegex(ch);
    }
  };

  const toAccentPattern = (text: string) => {
    return text.split('').map(charToPattern).join('');
  };

  const rawWords = query.trim().split(/\s+/).filter(w => w.length > 0);
  if (rawWords.length === 0) return null;

  try {
    if (rawWords.length === 1) {
      return new RegExp(`(${toAccentPattern(rawWords[0])})`, 'gi');
    }

    // Phrase complète ou mots significatifs (>= 2 lettres)
    const fullPhrasePattern = rawWords.map(toAccentPattern).join('\\s+');
    const wordPatterns = rawWords.filter(w => w.length >= 2).map(toAccentPattern);
    const combined = [fullPhrasePattern, ...wordPatterns].join('|');
    return new RegExp(`(${combined})`, 'gi');
  } catch (err) {
    return null;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Entoure tous les termes trouvés de balises <mark class="docarn-pdf-highlight">
 * pour surlignage haute visibilité en jaune dans le document PDF.
 */
export function highlightTextWithMark(str: string, query: string): string {
  if (!str || !query || query.trim().length === 0) return escapeHtml(str || '');
  const regex = createAccentInsensitiveRegex(query);
  if (!regex) return escapeHtml(str);

  let lastIdx = 0;
  let result = '';
  regex.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(str)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    if (matchStart > lastIdx) {
      result += escapeHtml(str.substring(lastIdx, matchStart));
    }

    result += `<mark class="docarn-pdf-highlight">${escapeHtml(match[0])}</mark>`;

    lastIdx = matchEnd;

    if (match[0].length === 0) {
      regex.lastIndex++;
    }
  }

  if (lastIdx < str.length) {
    result += escapeHtml(str.substring(lastIdx));
  }

  return result || escapeHtml(str);
}

/**
 * Recherche avancée de correspondances textuelles dans le texte extrait d'une page.
 * Supporte :
 * - Insensibilité complète aux accents (é, è, ê = e)
 * - Insensibilité à la casse (MAJ/min)
 * - Espacement flexible
 * - Génération d'extraits contextuels avec surbrillance
 */
export function findMatchesInPageText(
  pageText: string,
  query: string,
  pageNumber: number,
  isOcr: boolean = false,
  startCounter: number = 0
): { matches: TextSearchResult[]; nextCounter: number } {
  const matches: TextSearchResult[] = [];
  let counter = startCounter;

  if (!pageText || !query || query.trim().length === 0) {
    return { matches, nextCounter: counter };
  }

  const cleanQuery = query.trim().replace(/\s+/g, ' ');
  const normalizedQuery = cleanQuery
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const normalizedPageText = pageText
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  let pos = 0;
  while ((pos = normalizedPageText.indexOf(normalizedQuery, pos)) !== -1) {
    const start = Math.max(0, pos - 50);
    const end = Math.min(pageText.length, pos + cleanQuery.length + 50);

    const snippetBefore = (start > 0 ? '...' : '') + pageText.substring(start, pos);
    const matchText = pageText.substring(pos, pos + cleanQuery.length);
    const snippetAfter = pageText.substring(pos + cleanQuery.length, end) + (end < pageText.length ? '...' : '');

    counter++;
    matches.push({
      id: `match_${pageNumber}_${pos}_${counter}`,
      pageNumber,
      snippetBefore,
      matchText,
      snippetAfter,
      matchIndex: counter,
      isOcr
    });

    pos += Math.max(1, cleanQuery.length);
  }

  return { matches, nextCounter: counter };
}
